/*
 * Helper for the GDIPDPPage CMS layout.
 *
 * Each PDP CMS area (`BuyBoxAboveDescriptionContent`,
 * `BuyBoxBelowDescriptionContent`, `BelowProductInfoArea`, ...) is a list of
 * targeting wrappers. Each wrapper has the shape:
 *
 *   {
 *     AssignmentType: 'Category ID' | 'Product ID',
 *     ReferenceId: 'root' | '<categoryId>' | '<productId>',
 *     ApplyToSubcategories: boolean,
 *     GDIPDPPageContent: [
 *       {
 *         DisplayOption, Tag,
 *         ContentLink: { ..., Expanded: <GDI block> }
 *       }
 *     ]
 *   }
 *
 * Targeting rules:
 *   - `ReferenceId === 'root'` matches every product (lowest specificity /
 *     universal fallback), regardless of `AssignmentType`.
 *   - For `AssignmentType === 'Category ID'`:
 *       * exact match against `primaryCategoryId` wins.
 *       * an ancestor in `parentCategoryTree` matches only when
 *         `ApplyToSubcategories === true`.
 *       * the deepest matching wrapper wins; wrappers tied at the same depth
 *         all render (CMS author ordering preserved).
 *   - For `AssignmentType === 'Product ID'`:
 *       * `ReferenceId` is compared against the current product id, master
 *         id, and variant id. A direct id match is the most specific possible
 *         match and wins over every category wrapper.
 *
 * Raw GDI blocks placed directly into the area (i.e. items whose
 * `ContentLink.Expanded` is a renderable block rather than a targeting
 * wrapper) are always rendered as a fallback.
 */
const ROOT_REFERENCE_ID = 'root'
const ASSIGNMENT_CATEGORY = 'Category ID'
const ASSIGNMENT_PRODUCT = 'Product ID'

// Specificity depths. A product-id hit sits above any category depth.
const DEPTH_UNIVERSAL = 0
const DEPTH_PRODUCT_ID_MATCH = Number.MAX_SAFE_INTEGER

const buildCategoryChain = ({primaryCategoryId, parentCategoryTree}) => {
    const chain = []
    if (Array.isArray(parentCategoryTree)) {
        parentCategoryTree.forEach((entry) => {
            const id = typeof entry === 'string' ? entry : entry?.id
            if (id) chain.push(id)
        })
    }
    if (primaryCategoryId && chain[chain.length - 1] !== primaryCategoryId) {
        chain.push(primaryCategoryId)
    }
    return chain
}

const collectProductIds = (context) => {
    const ids = new Set()
    ;[context?.productId, context?.masterProductId, context?.variantProductId].forEach((id) => {
        if (id) ids.add(id)
    })
    if (Array.isArray(context?.productIds)) {
        context.productIds.forEach((id) => {
            if (id) ids.add(id)
        })
    }
    return ids
}

const getWrapperBlocks = (wrapper) => {
    const children = wrapper?.GDIPDPPageContent || []
    return children
        .map((child) => child?.ContentLink?.Expanded)
        .filter((expanded) => expanded && expanded.__typename)
}

const getWrapperDepth = (wrapper, {categoryChain, productIds}) => {
    const referenceId = wrapper?.ReferenceId
    if (!referenceId) return -1

    if (referenceId === ROOT_REFERENCE_ID) return DEPTH_UNIVERSAL

    const assignmentType = wrapper?.AssignmentType

    if (assignmentType === ASSIGNMENT_PRODUCT) {
        return productIds.has(referenceId) ? DEPTH_PRODUCT_ID_MATCH : -1
    }

    // Default to category-id matching (covers legacy/unspecified assignments).
    if (assignmentType && assignmentType !== ASSIGNMENT_CATEGORY) return -1

    const idx = categoryChain.indexOf(referenceId)
    if (idx === -1) return -1

    const isLeaf = idx === categoryChain.length - 1
    if (isLeaf) return idx + 1
    if (wrapper?.ApplyToSubcategories === true) return idx + 1
    return -1
}

const isWrapper = (item) =>
    item && typeof item === 'object' && (Array.isArray(item.GDIPDPPageContent) || item.ReferenceId)

export const extractPDPAreaBlocks = (areaItems, context = {}) => {
    if (!Array.isArray(areaItems)) return []

    const matchContext = {
        categoryChain: buildCategoryChain(context),
        productIds: collectProductIds(context)
    }

    const matchedWrappers = []
    const fallbackBlocks = []
    let bestDepth = -1

    areaItems.forEach((item) => {
        if (!item) return

        if (isWrapper(item)) {
            const depth = getWrapperDepth(item, matchContext)
            if (depth < 0) return

            if (depth > bestDepth) {
                bestDepth = depth
                matchedWrappers.length = 0
                matchedWrappers.push(item)
            } else if (depth === bestDepth) {
                matchedWrappers.push(item)
            }
            return
        }

        // Raw GDI block placed directly in the area — always render.
        const expanded = item?.ContentLink?.Expanded
        if (expanded && expanded.__typename) {
            fallbackBlocks.push(expanded)
        }
    })

    const blocks = matchedWrappers.flatMap(getWrapperBlocks)
    return [...fallbackBlocks, ...blocks]
}

